import { CheckCircle, Mic, Square, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { VoiceAgentState } from "./types";

interface VoiceAgentSheetProps {
  isOpen: boolean;
  state: VoiceAgentState;
  transcript: string;
  response: string | null;
  error: string | null;
  elapsedSeconds?: number;
  onStop: () => void;
  onNewRequest: () => void;
  onClose: () => void;
}

export function VoiceAgentSheet({
  isOpen,
  state,
  transcript,
  response,
  error,
  elapsedSeconds = 0,
  onStop,
  onNewRequest,
  onClose,
}: VoiceAgentSheetProps) {
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="flex flex-col h-64">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {state === "recording" && (
              <>
                <Mic className="h-4 w-4 animate-pulse text-primary" />
                Listening...
              </>
            )}
            {state === "processing" &&
              (elapsedSeconds > 0
                ? `Processing... (${elapsedSeconds}s)`
                : "Processing...")}
            {state === "response" && (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                Done
              </>
            )}
            {state === "error" && "Something went wrong"}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 py-2 overflow-auto">
          {state === "recording" && (
            <p className="text-muted-foreground text-sm min-h-[3rem]">
              {transcript || "Speak now..."}
            </p>
          )}
          {state === "processing" && (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
          {state === "response" && response && (
            <p className="text-sm">{response}</p>
          )}
          {state === "error" && error && (
            <p className="text-destructive text-sm">{error}</p>
          )}
        </div>

        <div className="flex gap-3 justify-center pb-4">
          {state === "recording" && (
            <button
              onClick={onStop}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-sm text-primary-foreground"
            >
              <Square className="h-4 w-4" />
              Stop
            </button>
          )}
          {(state === "response" || state === "error") && (
            <>
              <button
                onClick={onNewRequest}
                className="flex items-center gap-2 rounded-md border px-4 py-2 text-sm"
              >
                <Mic className="h-4 w-4" />
                New request
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
              >
                <X className="h-4 w-4" />
                Close
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
